import React, { useCallback, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import ContentPanel from "app/components/Layouts/ContentPanel";
import { useActions, useStoreActions } from "app/utils/hooks";
import { useDispatch } from "react-redux";
import {
  deleteGroup,
  fetchGroupById,
  putGroupSettings,
} from "app/actions/groups";
import { fetchSports } from "app/actions/sports";
import GroupsModel from "app/models/GroupsModel";
import customPrompt from "app/components/inputs/SelectBox/helpers/customPrompt";
import { Loading } from "app/components/Wrappers";
import CustomSelect from "app/components/inputs/CustomSelector";
import ButtonVariant from "app/components/Button/ButtonVariant";
import TransferGroupModal from "../_components/GroupTabsContainer/TransferGroupModal";
import GroupSettingsLayout from "../GroupSettingsLayout";
import TeamAvatarWithEdit from "app/components/TeamAvatarWithEdit";
import { Toast } from "app/components/helpers";

const GroupSettings = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const params = useParams<{ id: string }>();

  const dispatch = useDispatch();
  const teamActions = useActions({
    fetchGroupById,
    putGroupSettings,
    deleteGroup,
  });

  const route = (id: string) => {
    history.push(id);
  };

  const [groupData, setGroupData] = useState<GroupsModel>([]);
  const [changedData, setChangedData] = useState({});
  const { sports } = useStoreActions({ sports: fetchSports }, { noAuth: true });
  const [isLoading, setIsLoading] = useState(false);
  const [toastAlert, setToastAlert] = useState(null);
  const [toastErrorAlert, setToastErrorrAlert] = useState(null);

  const [modals, setModals] = useState({
    transfer: false,
  });

  useEffect(() => {
    teamActions.fetchGroupById(params.id).then((resp: GroupsModel) => {
      setGroupData(resp);

      if (resp) {
        setChangedData({ id: resp.id });
      }
    });
  }, [dispatch]);

  const handleModal = useCallback((key: string) => {
    setModals((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const onChangeHandler = (key: string, value: boolean | string) => {
    setGroupData((prevData: GroupsModel) => ({
      ...prevData,
      [key]: value,
    }));
    setChangedData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onDeleteGroup = () => {
    customPrompt({
      message: t(`Are you sure you would like to delete {{groupData}}?`, {
        groupData: groupData?.name,
      }),
      onYes: () => {
        teamActions.deleteGroup(groupData?.id).then(() => route("/groups"));
      },
      onCancel: () => {
        return;
      },
    });
  };

  const onUpload = useCallback(
    (data: { id: string; url: string }) => {
      setChangedData((prev) => ({
        ...prev,
        logo_id: data?.id,
        logo: data?.url,
      }));
      setGroupData({
        ...groupData,
        logo: data?.url,
        logo_id: data?.id,
      });
    },
    [groupData],
  );

  const onUpdateHandler = () => {
    setIsLoading(true);
    teamActions
      .putGroupSettings(changedData)
      .then(() => {
        teamActions.fetchGroupById(groupData?.id);
        setToastAlert(t("General settings were successfully updated."));
        setIsLoading(false);
      })
      .catch((err) => {
        setToastErrorrAlert(
          t(
            "An error occurred while updating Group Settings. Please try again later.",
          ),
        );
        console.error("Error while updating group general settings: ", err);
        setIsLoading(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const pendingButtons = !groupData || isLoading;

  return (
    <GroupSettingsLayout>
      <ContentPanel>
        <div className={styles.content_wrapper}>
          <TeamAvatarWithEdit avatar={groupData?.logo} onUpload={onUpload} />

          <div className={styles.text_input_wrapper}>
            <label htmlFor="name">{t("Group Name")}</label>
            <input
              disabled={pendingButtons || isLoading}
              type="text"
              name="name"
              id="name"
              defaultValue={groupData?.name ?? ""}
              placeholder={t("Name of a Group, organization, etc.")}
              onChange={(event) => onChangeHandler("name", event.target.value)}
            />
          </div>

          <div className={styles.text_input_wrapper}>
            <label htmlFor="name">{t("What is This For?")}</label>
            {!groupData || sports?.pending ? (
              <Loading isLoading loadType="spinner" />
            ) : (
              <div className={styles.sports_input_wrapper}>
                <CustomSelect
                  isDisabled={pendingButtons || isLoading}
                  placeholder={t("Select Sport / Subject")}
                  isCratable
                  options={sports?.data}
                  className={styles.sports_input}
                  value={
                    groupData?.sport
                      ? typeof groupData?.sport === "object"
                        ? groupData.sport
                        : {
                            label: groupData.sport,
                            value: groupData.sport,
                          }
                      : null
                  }
                  isClearable
                  isSearchable
                  onChange={(data: { value: string; label: string }) => {
                    onChangeHandler("sport", data?.value ? data?.value : null);
                  }}
                />
              </div>
            )}
          </div>
          <hr className={styles.border} />
          <div className={styles.options_container}>
            <label className={styles.label}>{t("Options")}</label>
            <p className={styles.options_description}>
              {t(
                "As the owner of this group, you have the ability to manage it.",
              )}
            </p>

            <div className={styles.option_buttons_contgainer}>
              <ButtonVariant
                title={t("Transfer Group Ownership")}
                buttonType="secondary-filled"
                childStyles={styles.transfer_btn_inner}
                buttonStyles={styles.transfer_btn}
                onClickFunc={() => handleModal("transfer")}
                disabled={pendingButtons || isLoading}
              />
              <ButtonVariant
                title={t("Delete")}
                buttonType="secondary-filled"
                childStyles={styles.delete_btn_inner}
                buttonStyles={styles.delete_btn}
                onClickFunc={onDeleteGroup}
                disabled={pendingButtons || isLoading}
              />
            </div>
          </div>
          <hr className={styles.border} />
          <ButtonVariant
            title={t("Save Changes")}
            buttonType="secondary-filled"
            childStyles={styles.save_btn_inner}
            buttonStyles={styles.save_btn}
            disabled={pendingButtons || isLoading}
            onClickFunc={onUpdateHandler}
          />
        </div>
      </ContentPanel>

      {modals.transfer && (
        <TransferGroupModal
          groupData={groupData}
          isModalOpen={modals.transfer}
          modalHandler={() => handleModal("transfer")}
        />
      )}
      <Toast
        success={toastAlert}
        error={toastErrorAlert}
        onDone={() => setToastAlert(null)}
      />
    </GroupSettingsLayout>
  );
};

export default GroupSettings;
